import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://srikalikamundeshwari.com/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      } else {
        setSubmitStatus('error');
        console.error('Server error:', result.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Network error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="animate-fadeIn py-8 md:py-16 bg-gradient-to-b from-orange-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-16 animate-slideUp">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4 text-gray-800 px-4">Contact Us</h1>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
            Get in touch with us for any inquiries, service bookings, or spiritual guidance. We're here to help you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 mb-8 md:mb-12">
          {/* Contact Form */}
          <div className="animate-slideLeft">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-5 sm:p-6 md:p-8 border border-orange-100">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-orange-600 flex items-center">
                <Mail className="mr-2 md:mr-3" size={24} />
                <span className="text-lg sm:text-xl md:text-2xl">Send us a Message</span>
              </h2>
              
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-green-50 border-l-4 border-green-500 rounded-lg animate-slideDown">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-2 md:mr-3 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-green-800 font-semibold text-sm md:text-base">Message Sent Successfully!</p>
                      <p className="text-green-600 text-xs md:text-sm mt-1">Thank you for contacting us. We'll get back to you soon.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border-l-4 border-red-500 rounded-lg animate-slideDown">
                  <div className="flex items-start">
                    <AlertCircle className="text-red-500 mr-2 md:mr-3 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-red-800 font-semibold text-sm md:text-base">Failed to Send Message</p>
                      <p className="text-red-600 text-xs md:text-sm mt-1">Please try again or contact us directly via phone or email.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 outline-none"
                    required
                    disabled={isSubmitting}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 outline-none"
                      required
                      disabled={isSubmitting}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 outline-none"
                      disabled={isSubmitting}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">Subject *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 outline-none"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="service">Service Booking</option>
                    <option value="wedding">Wedding Ceremony</option>
                    <option value="counseling">Spiritual Counseling</option>
                    <option value="donation">Donation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 outline-none resize-none"
                    required
                    disabled={isSubmitting}
                    placeholder="Please share your message or inquiry details..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 md:py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg flex items-center justify-center text-base md:text-lg ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transform hover:scale-[1.02] active:scale-[0.98]'
                  } text-white`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2" size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="animate-slideRight">
            {/* Contact Details Card */}
            <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white rounded-xl md:rounded-2xl shadow-xl p-5 sm:p-6 md:p-8 h-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-6">Get in Touch</h2>
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-start group">
                  <div className="bg-white bg-opacity-20 p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 flex-shrink-0 group-hover:bg-opacity-30 transition-all">
                    <MapPin size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-base md:text-lg mb-1">Address</p>
                    <p className="text-orange-50 leading-relaxed text-sm md:text-base">
                      No. 264-1, Nanjapuram, TVS Main Road, Kothogandapalli Post, Krishnagiri dist, Hosur - 635109
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start group">
                  <div className="bg-white bg-opacity-20 p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 flex-shrink-0 group-hover:bg-opacity-30 transition-all">
                    <Phone size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-base md:text-lg mb-1">Phone</p>
                    <a href="tel:+919845411941" className="text-orange-50 hover:text-white transition-colors text-sm md:text-base">
                      +91 98454 11941
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start group">
                  <div className="bg-white bg-opacity-20 p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 flex-shrink-0 group-hover:bg-opacity-30 transition-all">
                    <Mail size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-base md:text-lg mb-1">Email</p>
                    <a 
                      href="mailto:srikalikamundeshwaritemple@gmail.com" 
                      className="text-orange-50 hover:text-white transition-colors break-all text-sm md:text-base"
                    >
                      srikalikamundeshwaritemple@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start group">
                  <div className="bg-white bg-opacity-20 p-2.5 md:p-3 rounded-lg mr-3 md:mr-4 flex-shrink-0 group-hover:bg-opacity-30 transition-all">
                    <Clock size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-base md:text-lg mb-1">Temple Hours</p>
                    <p className="text-orange-50 text-sm md:text-base">6:00 AM - 9:00 PM</p>
                    <p className="text-orange-100 text-xs md:text-sm mt-1">(Open Daily)</p>
                  </div>
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="mt-6 md:mt-8 pt-6 border-t border-white border-opacity-30">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <a 
                    href="tel:+919845411941"
                    className="flex items-center justify-center bg-white text-orange-600 py-2.5 md:py-3 rounded-lg hover:bg-orange-50 transition-all duration-300 font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Phone className="mr-1.5 md:mr-2" size={16} />
                    <span className="hidden xs:inline">Call Now</span>
                    <span className="xs:hidden">Call</span>
                  </a>
                  <a 
                    href="mailto:srikalikamundeshwaritemple@gmail.com"
                    className="flex items-center justify-center bg-white text-orange-600 py-2.5 md:py-3 rounded-lg hover:bg-orange-50 transition-all duration-300 font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Mail className="mr-1.5 md:mr-2" size={16} />
                    <span className="hidden xs:inline">Email Us</span>
                    <span className="xs:hidden">Email</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map - Full Width */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden border border-orange-100 animate-slideUp">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 md:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center">
              <MapPin className="mr-2 flex-shrink-0" size={20} />
              <h3 className="font-semibold text-base md:text-lg">Find Us on the Map</h3>
            </div>
            
              <a href="https://www.google.com/maps/dir//Nanjapuram,+TVS+Main+Road,+Kothogandapalli+Post,+Krishnagiri+dist,+Hosur+-+635109"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center bg-white text-orange-600 px-3 md:px-4 py-2 rounded-lg hover:bg-orange-50 transition-all duration-300 font-semibold text-xs md:text-sm shadow-md w-full sm:w-auto"
            >
              <MapPin className="mr-1.5 md:mr-2 flex-shrink-0" size={14} />
              Get Directions
            </a>
          </div>
          <div className="relative h-64 sm:h-80 md:h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3890.0123456789!2d77.8263!3d12.7428!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDQ0JzM0LjEiTiA3N8KwNDknMzQuNyJF!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Sri Kalikamundeshwari Temple Location"
              className="absolute inset-0"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;